import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { FactoryTypeEnum } from '../../constants/enums';
import URLConstants from '../../constants/url-constants';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import IAddFactory from '../../models/forms/IAddUpdateFactory';
import { IObservableInitialState, IOption } from '../../models/ICommon';
import { IFactoryFilter } from '../../models/IFactoryFilter';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IFactoryList,
  IFactoryListVM,
  IFactoryVM,
} from '../../models/response/IFactoryResponse';
import { formatMessage } from '../../translations/format-message';
import { initialState as addFactoryInitialState } from '../initialState/add-factory-state';
import { initialState as allFactoryInitialState } from '../initialState/get-all-factory-state';
import * as baseService from '../service/base-service';

export class FactoryStore {
  initialStateValue: IObservableInitialState = {
    success: false,
    error: '',
    inProgress: false,
  };

  selectedFactoryId = -1;
  factoryDataList: IFactoryListVM = allFactoryInitialState;
  allFactorys: IFactoryVM[] = [];
  factory: any = undefined;
  addUpdateFactoryState = { ...this.initialStateValue };
  deleteFactoryState = { ...this.initialStateValue };
  getFactoryByIdServiceState = { ...this.initialStateValue };
  changePasswordState = { ...this.initialStateValue };
  getAllFactoryState = { ...this.initialStateValue };
  getAllFilteredFactoryState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      factory: observable,
      factoryDataList: observable,
      addUpdateFactoryState: observable,
      deleteFactoryState: observable,
      getFactoryByIdServiceState: observable,
      allFactorys: observable,
      getAllFactoryState: observable,
      AddFactoryService: action,
      UpdateFactoryService: action,
      GetFactoryListService: action,
      GetFactoryByIdService: action,
      GetAllFactoriesService: action,
      DeleteFactoryService: action,
      resetGetFactoryDetail: action,
      resetStore: action,
      reset: action,
      resetAddUpdateFactoryState: action,
      allFactorylist: computed,
      factoryDetails: computed,
      allAvailableFactories: computed,
      selectedFactoryId: observable,
      setSelectedFactoryId: action,
      FilterFactoryService: action,
      getAllFilteredFactoryState: observable,
    });
  }

  //#region Actions
  GetFactoryListService = (
    pageNumber: number,
    pageSize: number,
    filterQuery: string
  ) => {
    this.getAllFactoryState.inProgress = true;
    const url =
      URLConstants.FactoryList +
      '?PageNo=' +
      pageNumber +
      '&PageSize=' +
      pageSize +
      '&filterQuery=' +
      filterQuery;

    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IFactoryListVM>>) => {
        if (response.data.Error) {
          this.getAllFactoryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.factoryDataList = response.data.Data;
          this.getAllFactoryState.success = true;
        }
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
        this.factoryDataList = allFactoryInitialState;
      })
      .finally(
        action(() => {
          this.getAllFactoryState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to fetch the factory/tenant dropdown list from the Get All Tenants API.
  * @param allFactories: When true, always fetches every factory (backend only filters the
  * result down to the caller's own tenant when tenentId isn't -1) — used by cross-factory
  * pickers like Tool Management's Owner Factory and Borrower selects, which need every
  * factory regardless of which one the logged-in user belongs to.
  */
  GetAllFactoriesService = (allFactories: boolean = false) => {
    this.getAllFactoryState.inProgress = true;
    const tenantId = allFactories ? -1 : getTenantIdFromLocalStorage();
    const url =
      URLConstants.AllTenants +
      `/All?tenentId=${tenantId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.getAllFactoryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.allFactorys = response.data.Data;
          this.getAllFactoryState.success = true;
        }
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllFactoryState.inProgress = false;
        })
      );
  };
  GetFactoryByIdService = (factoryId: number) => {
    this.getFactoryByIdServiceState.inProgress = true;
    const url = URLConstants.GetFactoryById + '?TenantId=' + factoryId;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IFactoryVM>>) => {
        if (response.data.Error) {
          this.getFactoryByIdServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.factory = response.data.Data;
          this.getFactoryByIdServiceState.success = true;
        }
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getFactoryByIdServiceState.inProgress = false;
        })
      );
  };

  AddFactoryService = (factory: IAddFactory) => {
    this.addUpdateFactoryState.inProgress = true;
    return baseService
      .postRequest(URLConstants.AddFactory, factory)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateFactoryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.addUpdateFactoryState.success = true;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateFactoryState.inProgress = false;
        })
      );
  };

  UpdateFactoryService = (id: number, factory: any) => {
    this.addUpdateFactoryState.inProgress = true;
    return baseService
      .putRequest(URLConstants.UpdateFactory + '?id=' + id, factory)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateFactoryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.addUpdateFactoryState.success = true;
      })
      .catch((err: string) => {
        if (err.includes(':')) {
          let errorMess = err.split(':');
          toast.error(errorMess[0] + ' : ' + formatMessage(errorMess[1]));
        } else {
          toast.error(formatMessage(err));
        }
      })
      .finally(
        action(() => {
          this.addUpdateFactoryState.inProgress = false;
        })
      );
  };

  DeleteFactoryService = (id: number) => {
    this.deleteFactoryState.inProgress = true;
    const url = URLConstants.DeleteFactory + '/' + id;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteFactoryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteFactoryState.success = true;
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.deleteFactoryState.inProgress = false;
        })
      );
  };

  FilterFactoryService = (factory: IFactoryFilter) => {
    this.getAllFilteredFactoryState.inProgress = true;
    return baseService
      .postRequest(URLConstants.FilterTenantList, factory)
      .then((response: IApiResponse<IApiSuccessResponse<IFactoryListVM>>) => {
        if (response.data.Error) {
          this.getAllFilteredFactoryState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.factoryDataList = response.data.Data;
          this.getAllFilteredFactoryState.success = true;
        }
      })
      .catch((err: string) => {
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllFilteredFactoryState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region  Computed
  get allAvailableFactories(): IOption[] {
    const factoryOptions: IOption[] = [
      {
        id: -1,
        value: 'please_select',
        disabled: false,
      },
    ];
    if (this.allFactorys && this.allFactorys?.length > 0)
      this.allFactorys.map((factory) => {
        factoryOptions.push({
          id: Number(factory.TenantId),
          value: factory.FactoryName,
          disabled: false,
        });
      });
    return factoryOptions;
  }

  get allFactorylist(): IFactoryList[] {
    if (this.factoryDataList && this.factoryDataList?.Tenants.length > 0) {
      return this.factoryDataList?.Tenants.filter(
        (factory) => factory.TenantId >= 0
      ).map(
        ({
          TenantId,
          FactoryId,
          FactoryAddress,
          FactoryName,
          FactoryType,
          IsActive,
          FactoryCity,
          FactoryCountry,
        }) => {
          let isDelete = true;
          let isEdit = true;
          let isChangePassword = false;
          let accessFactory = true;

          return {
            TenantId,
            FactoryId,
            FactoryAddress,
            FactoryName,
            IsActive,
            FactoryType: formatMessage(FactoryTypeEnum[FactoryType]),
            FactoryCity,
            FactoryCountry,
          };
        }
      );
    }
    return [];
  }

  get factoryDetails(): any {
    if (this.factory) {
      const {
        TenantId,
        FactoryId,
        FactoryAddress,
        FactoryName,
        FactoryType,
        IsActive,
        FactoryCity,
        FactoryCountry,
      } = this.factory;
      return {
        TenantId,
        FactoryId,
        FactoryName,
        IsActive: false,
        FactoryAddress: FactoryAddress,
        FactoryType: 1,
        FactoryCity,
        FactoryCountry,
      };
    }
    return addFactoryInitialState;
  }

  get allFactoryOptions(): IOption[] {
    const options: IOption[] = [
      {
        id: -1,
        value: 'please_select',
      },
    ];
    if (this.allFactorys && this.allFactorys.length > 0)
      this.allFactorys.map((factory) => {
        options.push({
          id: Number(factory.FactoryId),
          value: factory.FactoryName,
        });
      });

    return options;
  }
  //#endregion

  //#region Reset
  resetGetFactoryDetail = () => {
    this.factory = undefined;
    this.getFactoryByIdServiceState = { ...this.initialStateValue };
  };

  resetAddUpdateFactoryState = () => {
    this.addUpdateFactoryState = { ...this.initialStateValue };
  };

  reset = () => {
    this.deleteFactoryState = { ...this.initialStateValue };
    this.addUpdateFactoryState = { ...this.initialStateValue };
    this.getAllFactoryState = { ...this.initialStateValue };
    this.changePasswordState = { ...this.initialStateValue };
  };

  resetStore = () => {
    this.factoryDataList = allFactoryInitialState;
    this.factory = undefined;
    this.addUpdateFactoryState = { ...this.initialStateValue };
    this.deleteFactoryState = { ...this.initialStateValue };
    this.getAllFactoryState = { ...this.initialStateValue };
    this.getFactoryByIdServiceState = { ...this.initialStateValue };
    this.changePasswordState = { ...this.initialStateValue };
    this.allFactorys = [];
    this.selectedFactoryId = -1;
    this.getAllFilteredFactoryState = { ...this.initialStateValue };
  };
  //#endregion

  //#region Other
  setSelectedFactoryId = (factoryId: number) => {
    this.selectedFactoryId = factoryId;
  };
  //#endregion
}

export default new FactoryStore();
